import React, { useState, useEffect, useCallback } from 'react';
import { FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getGuideVotes, submitGuideVote } from '../services/guideService';
import '../styles/GuideVoting.css';

const GuideVoting = ({ guidePath }) => {
    const { currentUser, idToken: token } = useAuth();
    const [votes, setVotes] = useState({ upvotes: 0, downvotes: 0 });
    const [userVote, setUserVote] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchVotes = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await getGuideVotes(guidePath, token); // Token can be null here
            setVotes({ upvotes: data.upvotes, downvotes: data.downvotes });
            setUserVote(data.userVote);
        } catch (err) {
            setError('Could not load vote count.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }, [guidePath, token]);

    useEffect(() => {
        fetchVotes();
    }, [fetchVotes]);

    const handleVote = async (voteType) => {
        if (!currentUser || !token || userVote) return;

        const originalVotes = { ...votes };
        const originalUserVote = userVote;

        const alreadyVoted = userVote === voteType;
        const newVoteCount = alreadyVoted ? votes[`${voteType}s`] - 1 : votes[`${voteType}s`] + 1;

        setVotes(prevVotes => ({
            ...prevVotes,
            [`${voteType}s`]: newVoteCount
        }));
        setUserVote(alreadyVoted ? null : voteType);
        setError('');

        try {
            await submitGuideVote(guidePath, voteType, token);
        } catch (err) {
            setError(err.message || 'Failed to submit vote.');
            setVotes(originalVotes);
            setUserVote(originalUserVote);
        }
    };
    
    if (isLoading) {
        return <div className="voting-container"><p>Loading feedback...</p></div>;
    }

    return (
        <div className="voting-container">
            <h4>Was this guide helpful?</h4>
            {error && <p className="vote-error">{error}</p>}
            <div className="vote-buttons">
                <button
                    onClick={() => handleVote('up')}
                    disabled={!currentUser || !!userVote}
                    className={`vote-button upvote ${userVote === 'up' ? 'voted' : ''}`}
                    aria-label="Upvote this guide"
                >
                    <FaArrowUp /> {votes.upvotes}
                </button>
                <button
                    onClick={() => handleVote('down')}
                    disabled={!currentUser || !!userVote}
                    className={`vote-button downvote ${userVote === 'down' ? 'voted' : ''}`}
                    aria-label="Downvote this guide"
                >
                    <FaArrowDown /> {votes.downvotes}
                </button>
            </div>
            {!currentUser && (
                <p className="login-prompt-text">
                    Please <Link to="/auth">log in</Link> to vote.
                </p>
            )}
            {currentUser && userVote && <p className="voted-message">Thanks for your feedback!</p>}
        </div>
    );
};

export default GuideVoting;
